<template>
    <section>
        <titlebar :title-stack="['Análise']" />
        <div class="section prd-page">
            <b-loading :active="carregando" :is-full-page="false" />
            <prd-empty-state
                v-if="!carregando && !erroCarregamento && !orcamentos.length"
                icon="clipboard-check-outline"
                title="Nenhum orçamento aguardando análise"
                description="Os orçamentos enviados pelo comercial aparecerão aqui."
            />

            <div v-for="orcamento in orcamentos" :key="orcamento.id" class="prd-card">
                <header><h2>{{ orcamento.clienteNome || 'Cliente não informado' }}</h2></header>
                <div class="prd-card-body prd-items">
                    <prd-empty-state v-if="!orcamento.itens.length" title="Nenhum item para analisar" description="O orçamento não possui itens nesta etapa." />
                    <article v-for="item in orcamento.itens" :key="item.id" class="prd-item">
                        <span class="prd-index">{{ item.sequencia }}</span>
                        <div class="prd-item-main">
                            <div class="prd-item-title">
                                <strong>{{ item.descricao }}</strong>
                                <prd-status-tag :status="item.statusProducao" tipo="itemAnalise" />
                            </div>
                            <p class="prd-muted">{{ resumoTecnico(item) || 'Sem especificações adicionais' }}</p>
                            <div class="prd-item-data">
                                <span>Quantidade: <b>{{ quantidade(item.quantidade) }} {{ item.unidadeMedida }}</b></span>
                                <span>Valor comercial: <b>{{ moeda(item.valorUnitarioComercial) }}</b></span>
                                <span>Prazo: <b>{{ item.prazoProducaoDias == null ? '—' : `${item.prazoProducaoDias} dias` }}</b></span>
                            </div>
                            <p v-if="item.observacoesProducao" class="prd-note-inline">{{ item.observacoesProducao }}</p>
                        </div>
                        <b-button
                            v-if="podeAlterar"
                            type="is-primary is-light"
                            size="is-small"
                            icon-left="pencil-outline"
                            :disabled="salvando"
                            @click="abrirItem(orcamento, item)"
                        >Atualizar</b-button>
                    </article>
                </div>
            </div>
        </div>

        <b-modal :active.sync="analiseModal" has-modal-card trap-focus>
            <prd-analise-item-modal
                v-if="analiseModal"
                :key="itemSelecionado.id"
                :item="itemSelecionado"
                @close="analiseModal = false"
                @salvar="salvarAnalise"
            />
        </b-modal>
    </section>
</template>

<script>
import http from '@/http'
import { mapGetters, mapState } from 'vuex'
import titlebar from '@/components/titlebar.vue'
import prdEmptyState from '@/components/prd/prdEmptyState.vue'
import prdStatusTag from '@/components/prd/prdStatusTag.vue'
import prdAnaliseItemModal from '@/components/prd/prdAnaliseItemModal.vue'

export default {
    name: 'PrdAnalise',
    components: { titlebar, prdEmptyState, prdStatusTag, prdAnaliseItemModal },
    data() {
        return {
            carregando: false,
            salvando: false,
            erroCarregamento: false,
            analiseModal: false,
            itemSelecionado: {},
            orcamentoSelecionadoId: null,
            orcamentos: [],
        }
    },
    computed: {
        ...mapState(['unidadeId']),
        ...mapGetters(['isInRole']),
        podeAlterar() { return this.isInRole('prd-analise-alterar') },
    },
    watch: {
        unidadeId() { this.carregar() },
        '$route.params.id'() { this.carregar() },
    },
    created() { this.carregar() },
    methods: {
        async carregar() {
            this.orcamentos = []
            this.erroCarregamento = false
            if (!this.unidadeId) return
            this.carregando = true
            try {
                const id = this.$route.params.id
                if (id) {
                    const resposta = await http.get(`/api/prd/analise/${id}?unidadeId=${this.unidadeId}`)
                    this.orcamentos = [resposta.data]
                    return
                }

                const resumos = []
                let pagina = 1
                let temMais = false
                do {
                    const resposta = await http.get(`/api/prd/analise?unidadeId=${this.unidadeId}&pagina=${pagina}&porPagina=100`)
                    const dados = resposta.data || {}
                    const itens = dados.itens || []
                    resumos.push(...itens)
                    temMais = itens.length > 0 && resumos.length < (dados.total || 0)
                    pagina++
                } while (temMais)

                const detalhes = await Promise.all(resumos.map((resumo) =>
                    http.get(`/api/prd/analise/${resumo.id}?unidadeId=${this.unidadeId}`)
                        .then((resposta) => resposta.data)
                        .catch((erro) => { if (erro.status === 404) return null; throw erro })
                ))
                this.orcamentos = detalhes.filter(Boolean)
            } catch (e) {
                this.erroCarregamento = true
                this.erro(e)
            } finally {
                this.carregando = false
            }
        },
        abrirItem(orcamento, item) {
            this.orcamentoSelecionadoId = orcamento.id
            this.itemSelecionado = item
            this.analiseModal = true
        },
        async salvarAnalise(item) {
            if (this.salvando) return
            this.salvando = true
            const orcamento = this.orcamentos.find((registro) => registro.id === this.orcamentoSelecionadoId)
            if (!orcamento) {
                this.analiseModal = false
                this.salvando = false
                return
            }
            try {
                const resposta = await http.put(
                    `/api/prd/analise/${orcamento.id}/itens/${item.id}?unidadeId=${this.unidadeId}`,
                    Object.assign({}, item, { versaoConcorrencia: orcamento.versaoConcorrencia })
                )
                this.analiseModal = false
                if (resposta.data?.finalizada) {
                    this.$buefy.toast.open({ message: 'Análise finalizada. O orçamento voltou para o comercial.', type: 'is-success' })
                    if (this.$route.params.id) await this.$router.replace({ name: 'prd-analise' })
                    else this.orcamentos = this.orcamentos.filter((registro) => registro.id !== orcamento.id)
                } else {
                    const atualizado = await http.get(`/api/prd/analise/${orcamento.id}?unidadeId=${this.unidadeId}`)
                    const indice = this.orcamentos.findIndex((registro) => registro.id === orcamento.id)
                    if (indice >= 0) this.$set(this.orcamentos, indice, atualizado.data)
                    this.$buefy.toast.open({ message: 'Análise do item atualizada.', type: 'is-success' })
                }
            } catch (e) {
                this.erro(e)
            } finally {
                this.salvando = false
            }
        },
        resumoTecnico(item) { return [item.material, item.dimensoes, item.processo, item.acabamento].filter(Boolean).join(' · ') },
        quantidade(valor) { return Number(valor || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 }) },
        moeda(valor) { return valor == null ? '—' : Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
        erro(e) { this.$buefy.toast.open({ message: e.data?.erro || 'Não foi possível concluir a operação.', type: 'is-danger' }) },
    },
}
</script>

<style scoped>
.prd-page{position:relative;background:var(--surface-page);min-height:calc(100vh - 110px);padding:1rem!important}
.prd-card{overflow:hidden;margin-bottom:1rem;border:1px solid var(--border-soft);border-radius:14px;background:#fff}
.prd-card>header{padding:1rem 1.2rem;border-bottom:1px solid #edf0f4}
.prd-card>header h2{font-weight:700;font-size:1.1rem}
.prd-card-body{padding:1.2rem}
.prd-items{display:grid;gap:.75rem}
.prd-item{display:flex;gap:.8rem;align-items:flex-start;padding:1rem;border:1px solid #e3e9f0;border-radius:12px}
.prd-index{display:grid;flex:0 0 32px;height:32px;place-items:center;border-radius:9px;background:var(--brand-teal-100);font-weight:700}
.prd-item-main{flex:1;min-width:0}
.prd-item-title{display:flex;justify-content:space-between;gap:1rem}
.prd-item-data{display:flex;gap:1rem;flex-wrap:wrap;margin-top:.65rem;padding:.65rem;background:#f7f9fc;font-size:.8rem}
.prd-muted{color:#8492a6;font-size:.8rem}
.prd-note-inline{margin-top:.5rem;color:#52606d;white-space:pre-line}
@media(max-width:800px){.prd-item,.prd-item-title{flex-direction:column}}
</style>
